import { useState, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';

// Define supported MIME types in order of preference
const MIME_TYPES = ['audio/webm', 'audio/mp4', 'audio/m4a'];

/**
 * Gets the first supported MIME type from the list of preferences
 * @returns The first supported MIME type
 * @throws Error if no supported format is found
 */
function getSupportedMimeType(): string {
  for (const type of MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) {
      logger.component('Using MIME type:', type);
      return type;
    }
  }
  throw new Error('No supported audio format found');
}

/**
 * Interface for the result of voice recording transcription
 */
export interface TranscriptionResult {
  /** The transcribed text from the audio recording */
  transcription: string;
}

/**
 * Return type for useVoiceRecording hook
 */
export interface UseVoiceRecordingReturn {
  /** Whether recording is currently in progress */
  isRecording: boolean;
  /** Current recording time in seconds */
  recordingTime: number;
  /** Maximum allowed recording time in seconds (5 minutes) */
  maxRecordingTime: number;
  /** Any error message during recording process */
  error: string | null;
  /** Start recording audio from the microphone */
  startRecording: () => Promise<void>;
  /** Stop recording and process audio for transcription */
  stopRecording: () => Promise<TranscriptionResult>;
  /** Cancel recording without processing */
  cancelRecording: () => void;
}

/**
 * Hook to handle voice recording functionality
 * Manages microphone access, recording state, and transcription
 *
 * @returns Object containing recording state and control methods
 */
export function useVoiceRecording(): UseVoiceRecordingReturn {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const recordedMimeType = useRef<string>('');

  // Maximum recording time in seconds (5 minutes)
  const maxRecordingTime = 300;

  /**
   * Starts recording audio from the microphone
   * @throws Error if microphone access fails
   */
  const startRecording = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
        },
        video: false,
      });

      const mimeType = getSupportedMimeType();
      recordedMimeType.current = mimeType;

      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      logger.component('MediaRecorder created', {
        mimeType: mediaRecorder.current.mimeType,
        state: mediaRecorder.current.state,
        audioBitsPerSecond: mediaRecorder.current.audioBitsPerSecond,
      });

      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (event): void => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.current.start(1000);
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      logger.error('Error starting recording:', error);
      setError('Could not access microphone. Please ensure microphone permissions are granted.');
      throw error;
    }
  }, []);

  /**
   * Stops recording and processes audio for transcription
   * @returns Promise resolving to transcription result
   */
  const stopRecording = useCallback(async (): Promise<TranscriptionResult> => {
    return new Promise((resolve, reject) => {
      if (!mediaRecorder.current || mediaRecorder.current.state === 'inactive') {
        setIsRecording(false); // Make sure UI is in non-recording state
        reject(new Error('No recording in progress'));
        return;
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      const handleStop = async (): Promise<void> => {
        try {
          logger.component('Processing audio chunks', {
            count: audioChunks.current.length,
          });

          const audioBlob = new Blob(audioChunks.current, {
            type: recordedMimeType.current || 'audio/webm',
          });

          if (audioBlob.size === 0) {
            // Ensure we clean up on error
            setIsRecording(false);
            mediaRecorder.current?.stream.getTracks().forEach(track => track.stop());
            throw new Error('No audio data recorded');
          }

          // Convert blob to base64
          const base64Data = await new Promise<string>((resolveConversion, rejectConversion) => {
            const reader = new FileReader();
            reader.onload = (): void => {
              if (typeof reader.result === 'string') {
                resolveConversion(reader.result);
              } else {
                rejectConversion(new Error('Failed to convert audio to base64'));
              }
            };
            reader.onerror = (): void => {
              rejectConversion(reader.error);
            };
            reader.readAsDataURL(audioBlob);
          });

          try {
            logger.ai('Sending audio for transcription', {
              audioDataSize: base64Data.length,
              recordingTime: recordingTime,
              mimeType: recordedMimeType.current,
            });

            const response = await fetch('/.netlify/functions/transcribe', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ audioBlob: base64Data }),
            });

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Transcription failed: ${errorText}`);
            }

            const result = await response.json();
            if (!result.text) {
              throw new Error('No transcription text in response');
            }

            const transcription = result.text;
            logger.component('Transcription successful', { length: transcription.length });
            logger.ai('Transcription complete', {
              textLength: transcription.length,
              recordingTime,
              wordsCount: transcription.split(/\s+/).length,
            });

            // Ensure all tracks are stopped
            mediaRecorder.current?.stream.getTracks().forEach(track => track.stop());

            setIsRecording(false);
            setError(null);
            resolve({ transcription });
          } catch (error) {
            // Make sure to clean up even on error
            mediaRecorder.current?.stream.getTracks().forEach(track => track.stop());
            setIsRecording(false);

            logger.error('Transcription error:', error);
            reject(error);
          }
        } catch (error) {
          // Make sure to clean up even on error
          mediaRecorder.current?.stream.getTracks().forEach(track => track.stop());
          setIsRecording(false);

          logger.error('Error processing audio:', error);
          setError('Failed to process audio. Please try again.');
          reject(error);
        }
      };

      mediaRecorder.current.onstop = handleStop;

      try {
        mediaRecorder.current.stop();
      } catch (error) {
        // Make sure to clean up even on error
        try {
          mediaRecorder.current?.stream.getTracks().forEach(track => track.stop());
        } catch (cleanupError) {
          logger.error('Error stopping tracks:', cleanupError);
        }

        setIsRecording(false);
        logger.error('Error stopping recording:', error);
        setError('Failed to stop recording. Please try again.');
        reject(error);
      }
    });
  }, [recordingTime]);

  /**
   * Cancels the current recording without processing
   */
  const cancelRecording = useCallback((): void => {
    if (!mediaRecorder.current) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      mediaRecorder.current.stop();
      mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setRecordingTime(0);
      setError(null);
    } catch (error) {
      logger.error('Error canceling recording:', error);
      setError('Failed to cancel recording');
    }
  }, []);

  return {
    isRecording,
    recordingTime,
    maxRecordingTime,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
